import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  IconButton,
  Autocomplete,
  Grid2,
  TextField,
} from "@mui/material";
import urlConstant from "../../constants/urlConstants";
import apiClient from "../../lib/api";
import { trackPromise } from "react-promise-tracker";
import dataConstant from "../../constants/dataConstants";
import { useNavigate } from "react-router-dom";
import GridComponent from "../../shared-components/gridComponent";
import moment from "moment";
import { IconCalendarWeek, IconEye } from "@tabler/icons-react";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import { selectUserDetail } from "../../redux/slice/userSlice";
import ForecastAnalysis from "../../components/dashboard/forecastAnalysis";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const today = dayjs();
const sevenDaysAgo = today.subtract(7, "day");
const Dashboard = () => {
  const [dailySubmissionRecords, setDailySubmissionRecords] = useState([]);
  const [gridKey, setGridKey] = useState(false);
  const selectUserDetails = useSelector(selectUserDetail);
  const [cityData, setCityData] = useState([]);
  const [advanceFiltersState, setAdvanceFiltersState] = useState({
    applicationDate: { from: sevenDaysAgo, to: today },
    cityId: null,
  });
  const [advanceFiltersValue, setAdvanceFiltersValue] = useState({
    applicationDate: { from: sevenDaysAgo, to: today },
    cityId: null,
  });
  const [snack, closeSnack, showSnackbar] = SnackState();
  const navigate = useNavigate();
  const columns = [
    {
      field: " ",
      headerName: "",
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            onClick={(e) => {
              if (selectUserDetails?.roles === dataConstant.roles.admin) {
                navigate(`/manageSubmission/${params.row.id}`);
              } else {
                navigate(`/submissionDetail/${params.row.id}`);
              }
            }}
          >
            <IconEye stroke={1.5} />
          </IconButton>
        </>
      ),
    },
    {
      field: "submissionId",
      headerName: "Submission No.",
      flex: 1,
      sortable: true,
    },
    {
      field: "fullName",
      headerName: "Full Name",
      flex: 1,
      sortable: true,
    },
    {
      field: "communityLiaison",
      headerName: "Community Liaison",
      flex: 1,
      sortable: false,
    },
    {
      field: "referredBy",
      headerName: "Referred By",
      flex: 1,
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: "applicationDate",
      headerName: "Application At",
      flex: 1,
      sortable: true,
    },
  ];

  const getGridData = async ({ page, pageSize, sortModel, searchText }) => {
    const sort =
      sortModel.length > 0
        ? {
            sortColumnName: sortModel[0].field,
            sortOrder: sortModel[0].sort === "asc" ? "asc" : "desc",
          }
        : { sortColumnName: "applicationDate", sortOrder: "desc" };
    const requestObject = {
      PagingInfoVM: {
        pageNumber: page,
        rowsPerPage: pageSize,
        searchText: searchText,
        sortColumns: sort,
      },
      FromDate : dayjs(advanceFiltersState?.applicationDate?.from).format(
        dataConstant.dateFormat
      ),
      ToDate: dayjs(advanceFiltersState?.applicationDate?.to).format(
        dataConstant.dateFormat
      ),
    };
    if(advanceFiltersState.cityId !== null){
      requestObject.CityUnqGUID = advanceFiltersState.cityId
    }
    try {
      const response = await trackPromise(
        apiClient.post(
          `${urlConstant.Dashboard.GetPendingSubmissionListByDaysBack}`,
          requestObject,
          { authRequired: true }
        )
      );

      if (response?.status !== dataConstant.APIStatus.Success) {
        showSnackbar(response.message, "error");
        return {
          items: [],
          totalCount: 0,
        };
      }
      const decryptedData = response.data?.pendingSubmissionLists?.map(
        (item) => ({
          ...item,
          id: item.unqGuid,
          applicationDate:
            item.applicationDate !== null
              ? moment
                  .utc(item.applicationDate)
                  .tz(dataConstant.centralAmericaTimeZone)
                  .format(dataConstant.dateFormatWithTime)
              : "-",
        })
      );
      return {
        items: decryptedData || [],
        totalCount: response.data?.totalCount || 0,
      };
    } catch (err) {
      console.error("API failed", err);
      return {
        items: [],
        totalCount: 0,
      };
    }
  };

  const getForecastAnalysisData = () => {
    const requestObject = {
      FromDate : dayjs(advanceFiltersState?.applicationDate?.from).format(
        dataConstant.dateFormat
      ),
      ToDate: dayjs(advanceFiltersState?.applicationDate?.to).format(
        dataConstant.dateFormat
      ),
    };
    if(advanceFiltersState.cityId !== null){
      requestObject.CityUnqGUID = advanceFiltersState.cityId
    }
    trackPromise(
      apiClient.post(urlConstant.Dashboard.GetDailySubmissionRecords, requestObject, {authRequired : true}).then((response) => {
        if(response?.status === dataConstant.APIStatus.Success){
          setDailySubmissionRecords(response.data)
        }else{
          showSnackbar(response?.message, "error")
        }
      })
    )
  }

  useEffect(() => {
    getForecastAnalysisData()
  },[advanceFiltersState])

  useEffect(() => {
    trackPromise(
      apiClient(urlConstant.City.GetAllCitiesWithoutStatusCheck, { authRequired: false }).then(
        (response) => {
          if (response?.status === dataConstant.APIStatus.Success) {
            setCityData(response.data);
          }
        }
      )
    );
  }, []);

  const renderFilter = () => {
    const applicationDateValue = advanceFiltersValue["applicationDate"] || "";
    const applicationFormDate = applicationDateValue?.from || "";
    const applicationToDate = applicationDateValue?.to || "";
    const isValidRange =
      applicationFormDate &&
      applicationToDate &&
      dayjs(applicationFormDate) <= dayjs(applicationToDate);
    const showError = applicationFormDate && applicationToDate && !isValidRange;
    return (
      <Grid2 container spacing={2} justifyContent="flex-end">
        <Grid2 item size={{ xs: 12, sm: 4, md: 3}}>
          <Autocomplete
            options={cityData}
            size="small"
            fullWidth
            value={
              cityData.find(
                (opt) => opt.unqGuid === advanceFiltersState.cityId
              ) || null
            }
            onChange={(_, newValue) => {
                setAdvanceFiltersState((prev) => ({
                  ...prev,
                  cityId: newValue?.unqGuid || null,
                }))
                setGridKey(!gridKey)
              }
            }
            getOptionLabel={(option) => option.cityName || ""}
            renderInput={(params) => (
              <TextField {...params} label="City" />
            )}
            isOptionEqualToValue={(option, val) =>
              option?.unqGuid === val?.unqGuid
            }
            getOptionKey={(option) => option?.unqGuid}
          />
        </Grid2>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid2
            item
            size={{ xs: 12, sm: 8, md: 6 }}
            display="flex"
            alignItems="stretch"
            gap={2}
          >
            <DatePicker
              label={`Application From`}
              value={applicationFormDate ? applicationFormDate : null}
              onChange={(newValue) => {
                const from = newValue ? newValue : "";
                const updated = {
                  ...advanceFiltersValue.applicationDate,
                  from,
                };
                setAdvanceFiltersValue((prev) => ({
                  ...prev,
                  applicationDate: updated,
                }));
                if (
                  from &&
                  applicationToDate &&
                  from <= dayjs(applicationToDate)
                ) {
                  setAdvanceFiltersState((prev) => ({
                    ...prev,
                    applicationDate: updated,
                  }));
                  setGridKey(!gridKey)
                }
              }}
              sx={{ width: "100%" }}
              slotProps={{
                textField: {
                  size: "small",
                  placeholder: "MM/DD/YYYY",
                  fullWidth: false,
                  readOnly: true,
                  error: showError,
                },
              }}
              slots={{
                openPickerIcon: () => <IconCalendarWeek stroke={1.5} />,
              }}
            />
            <DatePicker
              label={`Application To`}
              value={applicationToDate ? dayjs(applicationToDate) : null}
              onChange={(newValue) => {
                const to = newValue ? newValue : "";
                const updated = { ...advanceFiltersValue.applicationDate, to };
                setAdvanceFiltersValue((prev) => ({
                  ...prev,
                  applicationDate: updated,
                }));
                if (
                  applicationFormDate &&
                  to &&
                  applicationFormDate <= dayjs(to)
                ) {
                  setAdvanceFiltersState((prev) => ({
                    ...prev,
                    applicationDate: updated,
                  }));
                  setGridKey(!gridKey)
                }
              }}
              sx={{ width: "100%" }}
              slotProps={{
                textField: {
                  size: "small",
                  placeholder: "MM/DD/YYYY",
                  fullWidth: false,
                  readOnly: true,
                  error: showError,
                  helperText: showError
                    ? "To date must be after From date"
                    : "",
                },
              }}
              slots={{
                openPickerIcon: () => <IconCalendarWeek stroke={1.5} />,
              }}
            />
          </Grid2>
        </LocalizationProvider>
      </Grid2>
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      flex="1 1 0"
      gap={6}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap={{ xs: "wrap", sm: "nowrap" }}
        gap={1}
        mb={2}
      >
        <Typography variant="h6">Daily Client Submissions</Typography>
        {renderFilter()}
      </Box>
      <ForecastAnalysis data={dailySubmissionRecords} />

      <GridComponent
        columns={columns}
        getGridData={getGridData}
        gridKey={gridKey}
      />
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} />
    </Box>
  );
};

export default Dashboard;